import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authentication';
import { fetchUserPreferences, selectNShowcase } from '@/store/slices/userPreferences';
import { fetchUserSessions, logoutUserSession, logoutAllUserSessions } from '@/store/slices/userSessions';

import { PageShell, BackHeader } from '../../components';
import { ROUTE } from '../../routes';
import {
  sectionLabel,
  type SettingsSection,
  ProfileSection,
  InterfaceSection,
  CommsSection,
  PrivacySection,
  BillingSection,
  AccountSection,
  ContactSection,
  DeleteAccountSection,
} from './components';
import { requestAccountDeletion } from './api/deletionRequest';
import { useProfileForm, useCommsForm, useBillingForm, usePrivacyForm } from './useSettingsForms';
import { useBackToSettings } from '../../hooks/useBackToSettings';
import { CasinoSettingsSection } from '../casino-settings';
import { MailboxSection } from '../mailbox';
import { useMailboxEligibility } from '../mailbox/useMailboxEligibility';
import { SecureBrandSection } from '../secure-brand';
import { useBrandSecured } from '../secure-brand/useBrandSecured';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { SsoConnections } from '../social-login/components/SsoConnections';
import { SubPage, Contents } from './settings.styles';

export type SettingsSectionPageProps = {
  styleText?: string;
  className?: string;
};

const SECTION_KEYS: SettingsSection[] = [
  'secure-brand',
  'profile',
  'website-settings',
  'mailbox',
  'interface',
  'billing',
  'account',
  'communications',
  'privacy',
  'contact',
];
const isSection = (value: string | undefined): value is SettingsSection =>
  value !== undefined && (SECTION_KEYS as string[]).includes(value);

/**
 * A single Settings section, mounted at `/account/settings/:section`. Reads the
 * section from the URL (`useParams`) and renders it under the area's standard drill-in band:
 * `BackHeader` with the gradient title (`sectionLabel`) and the back arrow beside it, then a card
 * that carries only content, exactly like every other drill-in page in nova. The arrow goes
 * through `useBackToSettings`, so a deep link with no in-app history returns to the Settings
 * index instead of leaving the app. The list ↔ section slide is the area's route-level
 * `PageTransition`.
 *
 * The prefs-backed forms (profile / communications / billing) and the
 * account/devices wiring live here; the Website Settings section is a
 * self-contained component (its own state + mock endpoints) so its heavier logic
 * only runs when that section is active. Every section including Mailbox
 * is content-capped (via `Contents`); Website Settings alone renders full-bleed like
 * its old page.
 */
const SettingsSectionPage = ({
  styleText = '',
  className = '',
}: SettingsSectionPageProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const backToSettings = useBackToSettings();
  const { section: rawSection } = useParams<{ section: string }>();
  // Mailbox is gated on the `mailbox` feature flag (on for every owner, kept as the rollback switch).
  // Its data source is separate: live ows-mail wherever the config carries
  // `owsMailApiUrl`, otherwise the section itself says the service is not available (fixtures only when
  // a developer chose them; see `config/mailboxSource.ts`). With ows-mail configured, Mailbox shows only
  // once it says the brand's domain may have mail (`useMailboxEligibility`).
  // Secure your brand is gated the same way on `secure-brand` and stays off until its launch (the
  // production DocuSign account and the production test envelope).
  const { isEnabled } = useFeatureGate();
  const mailEligibility = useMailboxEligibility();

  // Hoisted to the component top: this hook must not be called conditionally inside the
  // `section === 'account'` JSX branch (rules-of-hooks). Sends the owner to the auth-gated
  // change-password page.
  const onChangePassword = React.useCallback(() => navigate(ROUTE.changePassword()), [navigate]);

  // While sliding OUT on a back-nav the `:section` param is already gone (the URL
  // is the index again), so keep showing the last valid section — otherwise the
  // exiting layer would render empty mid-slide.
  const paramSection = isSection(rawSection) ? rawSection : undefined;
  const lastValidRef = React.useRef<SettingsSection>('profile');
  React.useEffect(() => {
    if (paramSection) lastValidRef.current = paramSection;
  }, [paramSection]);
  const section = paramSection ?? lastValidRef.current;

  // Active logins ("devices") for this account + the token of the session this
  // browser is using (so the list can mark "This device").
  const currentToken = useAppSelector((state) => state.authentication.access_token);
  const sessions = useAppSelector((state) => state.userSessions.sessions);
  const sessionsLoading = useAppSelector((state) => state.userSessions.loading);
  const sessionsLoaded = useAppSelector((state) => state.userSessions.loaded);
  const sessionsError = useAppSelector((state) => state.userSessions.error);
  const revokingToken = useAppSelector((state) => state.userSessions.revokingToken);
  const revokingAll = useAppSelector((state) => state.userSessions.revokingAll);

  // The active brand's display name personalises the Website Settings drill-in
  // title ("NeonJack Settings"); undefined → static "Website Settings" fallback.
  const brandName = useAppSelector((state) => state.casinoInstance.activeBrand?.casino_data?.details?.brand);

  // Once the brand's declaration is signed, the Secure your brand title says so ("Your NeonJack brand
  // is secured"), like the Settings row. No read of its own: the section's status read feeds it.
  const brandSecured = useBrandSecured(false);

  // The owner's saved preferences back the forms below. MainLayout already loads
  // them app-wide; this guarded dispatch makes the page self-sufficient if it's
  // ever rendered without that layout, and is a no-op once loaded/loading.
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  const prefsLoading = useAppSelector((state) => state.userPreferences.loading);
  React.useEffect(() => {
    if (!prefsLoaded && !prefsLoading) {
      dispatch(fetchUserPreferences());
    }
  }, [prefsLoaded, prefsLoading, dispatch]);

  // Prefs-backed forms (profile / communications / billing): seed from the
  // preferences slice, track their own dirty + save state, and persist via the API.
  const profile = useProfileForm();
  const comms = useCommsForm();
  const privacy = usePrivacyForm();
  const billing = useBillingForm();

  // Load the active sessions the first time the account section is shown.
  React.useEffect(() => {
    if (section === 'account' && !sessionsLoaded && !sessionsLoading) {
      dispatch(fetchUserSessions());
    }
  }, [section, sessionsLoaded, sessionsLoading, dispatch]);

  // Revoke a single OTHER login (the current device signs out via onSignOut).
  // A login can carry several tokens (access + refresh) — revoke them all so the
  // row doesn't reappear when its twin survives.
  const onRevoke = React.useCallback(
    (tokens: string[]) => {
      tokens.forEach((token) => dispatch(logoutUserSession({ token })));
    },
    [dispatch],
  );

  // Sign out THIS device: revoke all of the current login's server tokens
  // (best-effort — the revoke needs the auth header, so await them before
  // clearing local tokens), then drop this browser's stored tokens and leave.
  const onSignOut = React.useCallback(
    async (tokens: string[]) => {
      await Promise.all(
        tokens.map((token) =>
          dispatch(logoutUserSession({ token }))
            .unwrap()
            .catch(() => undefined),
        ),
      );
      dispatch(logout());
      navigate('/account/home');
    },
    [dispatch, navigate],
  );

  // Sign out EVERY device, then clear this browser's tokens and leave.
  const onLogoutAll = React.useCallback(async () => {
    await dispatch(logoutAllUserSessions())
      .unwrap()
      .catch(() => undefined);
    dispatch(logout());
    navigate('/account/home');
  }, [dispatch, navigate]);

  // Profile's "Delete your account" card: the backend emails the owner (and the data protection
  // team) about the request; nothing is deleted here. Hoisted with the other hooks (rules-of-hooks).
  // The email only names the address in the card's confirmation popup.
  const accountEmail = useAppSelector((state) => state.authentication.user.email);
  const onRequestDeletion = React.useCallback(() => requestAccountDeletion(currentToken), [currentToken]);
  // The Newton showcase hides Billing Information (see SettingsList).
  const newtonShowcase = useAppSelector(selectNShowcase);

  // A genuinely unknown section in the URL (e.g. a stale/typo'd link) → bounce to
  // the Settings index. Placed after every hook so the hook order never varies.
  if (rawSection !== undefined && !isSection(rawSection)) {
    return <Navigate to={ROUTE.settings()} replace />;
  }

  // A direct hit on /account/settings/mailbox or /account/settings/secure-brand while that
  // section's flag is off (e.g. a stale link in production) bounces to the Settings index rather
  // than rendering a gated section. So does Mailbox once ows-mail has not said the domain may have mail.
  if (
    section === 'mailbox' &&
    (!isEnabled('mailbox') || mailEligibility === 'not_eligible' || mailEligibility === 'unknown')
  ) {
    return <Navigate to={ROUTE.settings()} replace />;
  }
  // A direct link waits for the answer showing nothing, not even the section's header: the section never
  // appears (or reads) for a domain that goes.
  if (section === 'mailbox' && mailEligibility !== 'eligible') return null;
  if (section === 'secure-brand' && !isEnabled('secure-brand')) {
    return <Navigate to={ROUTE.settings()} replace />;
  }
  if (section === 'billing' && newtonShowcase) {
    return <Navigate to={ROUTE.settings()} replace />;
  }

  return (
    <PageShell styleText={styleText} className={className}>
      <SubPage>
        {/* Website Settings reads "{Brand} Settings", Secure your brand says when it is secured (sectionLabel personalises both). */}
        <BackHeader title={sectionLabel(t, section, brandName, brandSecured)} onBack={backToSettings} />
        {section === 'website-settings' ? (
          <CasinoSettingsSection />
        ) : section === 'secure-brand' ? (
          <Contents>
            <SecureBrandSection />
          </Contents>
        ) : section === 'mailbox' ? (
          <Contents>
            <MailboxSection />
          </Contents>
        ) : (
          <Contents>
            {section === 'profile' && (
              <ProfileSection
                values={profile.values}
                dirty={profile.dirty}
                saving={profile.saving}
                saveError={profile.saveError}
                justSaved={profile.justSaved}
                onChange={profile.onChange}
                onSave={profile.save}
              />
            )}
            {section === 'profile' && <DeleteAccountSection email={accountEmail} onRequest={onRequestDeletion} />}
            {section === 'interface' && <InterfaceSection />}
            {section === 'communications' && (
              <CommsSection
                comms={comms.comms}
                dirty={comms.dirty}
                saving={comms.saving}
                saveError={comms.saveError}
                justSaved={comms.justSaved}
                onToggle={comms.toggle}
                onSave={comms.save}
              />
            )}
            {section === 'privacy' && (
              <PrivacySection
                marketing={privacy.marketing}
                dirty={privacy.dirty}
                saving={privacy.saving}
                saveError={privacy.saveError}
                justSaved={privacy.justSaved}
                onToggle={privacy.toggle}
                onSave={privacy.save}
              />
            )}
            {section === 'billing' && (
              <BillingSection
                values={billing.values}
                errors={billing.errors}
                dirty={billing.dirty}
                saving={billing.saving}
                saveError={billing.saveError}
                justSaved={billing.justSaved}
                onChange={billing.onChange}
                onToggleMethod={billing.onToggleMethod}
                onSave={billing.save}
              />
            )}
            {section === 'account' && (
              <AccountSection
                sessions={sessions}
                currentToken={currentToken}
                loaded={sessionsLoaded}
                error={sessionsError}
                revokingToken={revokingToken}
                revokingAll={revokingAll}
                onRevoke={onRevoke}
                onSignOut={onSignOut}
                onLogoutAll={onLogoutAll}
                onChangePassword={onChangePassword}
                connectionsSlot={<SsoConnections />}
              />
            )}
            {section === 'contact' && <ContactSection />}
          </Contents>
        )}
      </SubPage>
    </PageShell>
  );
};

export default SettingsSectionPage;
